import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { typedFetch } from '../../typedFetch';
import { PrettyDate } from '../PrettyDate';
import { STATUSES } from '../STATUSES';
import './Ticket.scss';
const requesterToken = new URLSearchParams(window.location.search).get('requester_token');
const ticketIdFromURL = Number(window.location.pathname.match(/^\/tickets\/(\d+)/)?.[1]);
const ImageWithFallback = ({ ticketId, imageNumber, }) => {
    const extensions = [
        '.png',
        '.jpg',
        '.jpeg',
        '.gif',
        '.webp',
    ];
    const [currentExtIndex, setCurrentExtIndex] = useState(0);
    const currentExt = extensions[currentExtIndex];
    return (_jsx("div", { className: "image-container", children: _jsx("img", { src: `/images/${ticketId}-${imageNumber}${currentExt}`, alt: `Ticket image ${imageNumber}`, onError: () => {
                if (currentExtIndex < extensions.length - 1) {
                    setCurrentExtIndex(currentExtIndex + 1);
                }
            }, style: {
                maxWidth: '100%',
                height: 'auto',
                cursor: 'pointer',
                display: currentExtIndex === extensions.length - 1 ? 'none' : 'block',
            }, onClick: () => {
                window.open(`/images/${ticketId}-${imageNumber}${currentExt}`, '_blank');
            } }) }));
};
export const Ticket = ({ appendToErrorLog, loggedInUser, }) => {
    // URL like ticket/2
    // display info only if logged in
    // or if URL is ticket/2?token=guid????
    // any way to not save token in database? maybe something with submit timestamp encoding along with current step number, plus salt?
    const [loading, setLoading] = useState(true);
    const [ticket, setTicket] = useState();
    const [updateTicketStatusOptions, setUpdateTicketStatusOptions] = useState([]);
    const [updateTicketStatusValue, setUpdateTicketStatusValue] = useState('On hold');
    const [notes, setNotes] = useState('');
    const [showRelativeDates, setShowRelativeDates] = useState(localStorage.getItem('showRelativeDates') === 'yes');
    const [availableWorkers, setAvailableWorkers] = useState([]);
    const [newWorkerName, setNewWorkerName] = useState('');
    useEffect(() => {
        localStorage.setItem('showRelativeDates', showRelativeDates ? 'yes' : '');
    }, [showRelativeDates]);
    useEffect(() => {
        if (!loggedInUser && !requesterToken) {
            appendToErrorLog('Tickets are only visible to logged-in users, or to those clicking on the links in the emails');
            return;
        }
        if (!ticketIdFromURL) {
            appendToErrorLog('Ticket ID not found in URL');
            return;
        }
        typedFetch('ticket', {
            body: { ticketId: ticketIdFromURL, requesterToken, action: 'get' },
        }, 'no-auth-needed')
            .then((result) => {
            if (!result.success) {
                appendToErrorLog(`Failed to retrieve ticket: ${result.reason}`);
                return;
            }
            setTicket(result.ticket);
            setUpdateTicketStatusValue(result.ticket.status);
            setUpdateTicketStatusOptions(loggedInUser?.supervisor
                ? STATUSES
                : loggedInUser?.departments.includes(result.ticket.department)
                    ? [
                        'Accepted',
                        'In progress',
                        'Completed',
                        'On hold',
                        'Wontfix',
                    ]
                    : [
                        'Request canceled',
                        'Verified',
                        'Work not accepted',
                    ]);
            setUpdateTicketStatusOptions(options => options.includes(result.ticket.status) ? options : [...options, result.ticket.status]);
            setAvailableWorkers(loggedInUser?.supervisor
                ? ['', ...result.allWorkerNames]
                : loggedInUser
                    ? ['', loggedInUser.name]
                    : ['']);
            setNewWorkerName(result.ticket.workerName);
        })
            .catch((error) => {
            appendToErrorLog(`Unexpected error while retrieving ticket: ${String(error['message'])}`);
        })
            .finally(() => {
            setLoading(false);
        });
    }, [appendToErrorLog, loggedInUser]);
    useEffect(() => {
        if (!ticket || !loggedInUser) {
            return;
        }
        const newStatusesIndicatingWorker = [
            'Accepted',
            'In progress',
            'Completed',
        ];
        if (updateTicketStatusValue !== ticket.status && !ticket.workerName && newStatusesIndicatingWorker.includes(updateTicketStatusValue) && loggedInUser.departments.includes(ticket.department)) {
            setNewWorkerName(loggedInUser.name);
        }
        else if (updateTicketStatusValue === ticket.status || !newStatusesIndicatingWorker.includes(updateTicketStatusValue)) {
            setNewWorkerName(ticket.workerName);
        }
    }, [ticket, updateTicketStatusValue, loggedInUser]);
    const updateTicket = (e) => {
        e.preventDefault();
        setLoading(true);
        typedFetch('ticket', {
            body: {
                ticketId: ticketIdFromURL,
                requesterToken,
                action: 'update',
                newStatus: updateTicketStatusValue, notes,
                newWorkerName: newWorkerName === ticket?.workerName ? '' : newWorkerName,
            },
            timeout: 10_000, // Sending emails to multiple may take a number of seconds of waiting
        }, 'no-auth-needed')
            .then((result) => {
            if (result.success) {
                const url = new URL(window.location.href);
                url.searchParams.set('scrollBottom', '1');
                window.location.href = url.toString();
            }
            else {
                appendToErrorLog(`Unexpected error while updating ticket: ${result.reason}`);
            }
        })
            .catch((error) => {
            appendToErrorLog(`Unexpected error while updating ticket: ${String(error['message'])}`);
        })
            .finally(() => {
            setLoading(false);
        });
    };
    useLayoutEffect(() => {
        if (ticket && (new URL(window.location.href)).searchParams.get('scrollBottom') === '1') {
            window.scrollTo(0, document.body.scrollHeight);
        }
    }, [ticket]);
    if (loading) {
        return 'Loading ticket...';
    }
    if (!ticket) {
        return 'No ticket to display';
    }
    return (_jsxs("main", { className: "ticket-page", children: [_jsx("h1", { children: `#${ticket.id}: ${ticket.title}` }), _jsxs("h3", { className: "intro", children: ["A ticket from", _jsxs("div", { children: [_jsx("div", { children: ticket.requesterName }), _jsx("div", { children: ticket.requesterEmail })] }), "for", ` ${ticket.department}${ticket.workerName ? ` (${ticket.workerName})` : ''}`] }), ticket.scheduleId ? _jsxs("p", { children: ["From Schedule ", _jsxs("a", { href: `/schedules/${ticket.scheduleId}`, children: ["#", ticket.scheduleId] })] }) : null, _jsxs("p", { children: ["Requested completion date: ", _jsx(PrettyDate, { timestamp: ticket.requestedCompletionTimestamp, showRelativeDates, includeTime: false })] }), ticket.actualCompletionTimestamp ? _jsxs("h4", { children: ["Actual completion date: ", _jsx(PrettyDate, { timestamp: ticket.actualCompletionTimestamp, showRelativeDates })] }) : null, ticket.imageCount && ticket.imageCount > 0 ? (_jsxs("div", { className: "ticket-images", children: [_jsx("h3", { children: "Attached Images" }), _jsx("div", { className: "images-grid", children: Array.from({ length: ticket.imageCount }, (_, i) => (_jsx(ImageWithFallback, { ticketId: ticket.id, imageNumber: i + 1 }, i + 1))) })] })) : null, _jsxs("h3", { className: "timeline", children: [_jsx("div", {}), "Timeline", _jsx("div", { children: _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: showRelativeDates, onChange: (e) => { setShowRelativeDates(e.currentTarget.checked); } }), "Relative dates"] }) })] }), _jsx("div", { className: "history", children: ticket.history.map(historyStep => (_jsxs("div", { children: [_jsx("div", { children: _jsx(PrettyDate, { timestamp: historyStep.timestamp, showRelativeDates }) }), _jsx("div", { children: historyStep.updateType }), _jsx("div", { children: historyStep.updater }), _jsxs("div", { children: [(historyStep.newWorkerName ? _jsxs(_Fragment, { children: ["Assigned to ", historyStep.newWorkerName, _jsx("br", {}), " ", _jsx("br", {})] }) : ''), historyStep.notes.match(/.+/g)?.map((line, index) => _jsx("div", { children: line }, index))] })] }, historyStep.timestamp))) }), _jsxs("form", { onSubmit: updateTicket, children: [_jsx("h3", { children: "You may update the ticket status or add notes:" }), _jsxs("div", { className: "choose-status", children: [_jsx("select", { value: updateTicketStatusValue, onChange: (e) => { setUpdateTicketStatusValue(e.currentTarget.value); }, children: updateTicketStatusOptions.map(option => _jsx("option", { value: option, children: option }, option)) }), _jsxs("a", { href: "/flowchart.png", target: "_blank", children: [_jsx("div", { children: "Unsure how this works? Ticket status flowchart:" }), _jsx("img", { src: "/flowchart.png" })] })] }), loggedInUser ? (_jsxs("div", { className: "choose-worker", children: ["Worker:", _jsx("select", { value: newWorkerName, onChange: (e) => { setNewWorkerName(e.currentTarget.value); }, children: availableWorkers.map(workerName => _jsx("option", { value: workerName, children: workerName }, workerName)) })] })) : null, _jsx("textarea", { placeholder: "Put your notes, updates, or explanations here", value: notes, onChange: (e) => { setNotes(e.currentTarget.value); } }), _jsx("button", { disabled: ticket.status === updateTicketStatusValue && notes.length < 10 && ticket.workerName === newWorkerName, children: "Update ticket" })] })] }));
};
